import React, { useMemo } from 'react';
import { fmtDatumNL } from '../util/format';
import { MELDING_DOELEN } from '../util/doelen';
import PuttenPaneel from './PuttenPaneel';
import PuttenKaart from './PuttenKaart';

/* MeldingFormulier — het formulier rondom het puttenpaneel, voor het aanmaken
   of bijwerken van een melding-inrichting.

   ── Waarom dit één component is ───────────────────────────────────────────

   Dezelfde melding werd op drie plekken opgevoerd, met drie verschillende
   formulieren eromheen: een hele pagina onder Beheertools, een modal op een
   bestaande inrichting, en een pagina vanaf een houder. Dezelfde velden, drie
   volgordes, drie manieren om de meldingsdatum af te leiden, drie keer een
   ander woord voor hetzelfde. Nu is het één formulier met kaarten die je aan-
   of uitzet, zodat de verschillen alleen nog gaan over wat er écht anders is.

   ── Drie modi ─────────────────────────────────────────────────────────────

   'controleren'  de melding komt uit een bijlage. Wat de melder heeft
                  opgegeven staat vast en is leestekst; het kenmerk vul je zelf.
   'bewerken'     je voert de melding zelf op, bijvoorbeeld na een papieren
                  melding. Dan is alles invulbaar.
   'bekijken'     een melding die al is vastgelegd maar nog niet geldt. Je leest
                  hem eerst; bewerken is een aparte, bewuste stap.

   ── Indeling ──────────────────────────────────────────────────────────────

   Melding        soort kenmerk, kenmerk, meldingsdatum. Dit gaat over de
                  mélding: waaronder is hij binnengekomen en wanneer.
   Gebruiksrecht  inwerkingtreding, doel, maximale pompcapaciteit,
                  bedrijfsomvang. Dit gaat over het récht dat uit die melding
                  voortkomt. De inwerkingtreding hoort hier en niet bij de
                  melding: het is de datum waarop het recht ingaat, niet de
                  datum waarop de melding binnenkwam.
   Putten         het puttenpaneel.

   ── Geen uitlegregels ─────────────────────────────────────────────────────

   Eerder stond onder half de velden een zin die vertelde wat er gebeurde. Die
   zijn eruit. Waar het gedrag toch duidelijk moet zijn, doet de vórm het werk:
   de meldingsdatum is bij een verzoeknummer geen invoerveld maar platte tekst,
   en dát zegt al dat hij wordt afgeleid en niet ingetypt. */

/* De drie doelen die de vrijstelling kent, uit util/doelen. Hier stond een
   eigen kopie met dezelfde teksten; die liep het risico apart te verouderen.
   Voluit, want dit scherm spiegelt stap 1 van het meldingsformulier: de
   behandelaar vinkt hier aan wat de melder daar heeft aangevinkt. */
const DOELEN = MELDING_DOELEN.map((d) => ({ waarde: d.waarde, label: d.voluit, pomp: d.pomp }));

/* Eén datumnotatie in de hele applicatie: DD maand JJJJ, uit util/format.
   Hier stond een tweede exemplaar met een eigen maandenlijst. Het verschil met
   fmtDatumNL is alleen de lege waarde: Feit hieronder tekent zelf een streepje,
   dus die moet null krijgen en niet '—'. */
export const datumNL = (iso) => (iso ? fmtDatumNL(iso) : null);

/* Meldingsdatum uit de eerste acht cijfers van het verzoeknummer (JJJJMMDD).
   Exact zoals de backend het doet: alle niet-cijfers eruit, dan de eerste acht.
   Eén afleiding op één plek — dat waren er eerst drie, elk net iets anders. */
export const datumUitKenmerk = (kenmerk) => {
  const cijfers = (kenmerk || '').replace(/\D/g, '');
  if (cijfers.length < 8) return null;
  const j = cijfers.slice(0, 4), m = cijfers.slice(4, 6), d = cijfers.slice(6, 8);
  const jaar = +j, maand = +m, dag = +d;
  if (jaar < 2020 || jaar > 2100 || maand < 1 || maand > 12 || dag < 1 || dag > 31) return null;
  return `${j}-${m}-${d}`;
};

// Inwerkingtreding = meldingsdatum + 28 dagen.
export const inwerkingtredingVan = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  d.setDate(d.getDate() + 28);
  return d.toISOString().slice(0, 10);
};

function Kaart({ titel, children }) {
  return (
    <div className="card mf-kaart">
      {titel && <div className="card-header"><h3 className="card-title">{titel}</h3></div>}
      {children}
    </div>
  );
}

// Leesregel: label boven, waarde eronder. Voor alles wat vaststaat of afgeleid is.
function Feit({ label, waarde }) {
  return (
    <div className="mf-veld">
      <span className="mf-label">{label}</span>
      <span className="mf-waarde">{waarde ?? <span className="mf-leeg">—</span>}</span>
    </div>
  );
}

function Veld({ label, children }) {
  return (
    <div className="mf-veld">
      <label className="mf-label">{label}</label>
      {children}
    </div>
  );
}

/* Gewone keuzerondjes, geen kaartjes met randen eromheen. De doelen zijn hele
   zinnen; die passen niet in knoppen naast elkaar en worden in een uitklaplijst
   pas zichtbaar nadat je hem opent. Onder elkaar met een rondje ervoor lees je
   ze gewoon. */
function Rondjes({ naam, opties, waarde, onChange, richting = 'onder' }) {
  return (
    <div className={`mf-rondjes mf-rondjes--${richting}`}>
      {opties.map((o) => (
        <label key={o.waarde} className={`mf-rondje ${waarde === o.waarde ? 'is-aan' : ''}`}>
          <input type="radio" name={naam} checked={waarde === o.waarde}
            onChange={() => onChange(o.waarde)} />
          <span>{o.label}</span>
        </label>
      ))}
    </div>
  );
}

export default function MeldingFormulier({
  modus = 'bewerken',            // 'controleren' | 'bewerken' | 'bekijken'
  bron = null,                   // { bestand, herkomst: 'json' | 'tekst' }
  waarden,
  onChange,
  putten,
  onPuttenChange,
  registerPutten = [],
  gebieden = [],
  houder = null,
  inrichtingKenmerk = null,
  titel = 'Nieuwe melding verwerken',
  /* Op een pagina staan titel, houder en herkomst al in de paginakop. Dan zet
     dit formulier er geen tweede kop bovenop. */
  badge = true,
  primair = 'Melding vastleggen',
  onAnnuleer,
  onOpslaan,
  bezig = false,
  fout = null,
  /* Een derde knop links in de voet, voor een actie die geen opslaan is —
     nu alleen "Melding intrekken" bij het bekijken van een klaarstaande
     melding. */
  extraActie = null,             // { label, onClick, gevaarlijk }
}) {
  const bekijken = modus === 'bekijken';
  // Alles wat in 'controleren' leestekst is, is dat in 'bekijken' ook.
  const controleren = modus === 'controleren' || bekijken;
  const zet = (velden) => onChange({ ...waarden, ...velden });

  const meldingsdatum = waarden.kenmerkType === 'verzoeknummer'
    ? datumUitKenmerk(waarden.kenmerk)
    : waarden.meldingsdatum;
  const inwerking = inwerkingtredingVan(meldingsdatum);
  const doel = DOELEN.find((d) => d.waarde === waarden.doel);

  const bijlage = waarden.doel === 'grasland' ? 11
    : (waarden.doel === 'gewas' || waarden.doel === 'beide') ? 12 : null;

  const compleet = useMemo(() => {
    if (!String(waarden.kenmerk || '').trim()) return false;
    if (!meldingsdatum) return false;
    if (!putten.length) return false;
    return !putten.some((p) => p.soort === 'nieuw' && !String(p.max_diepte_mv || '').trim());
  }, [waarden.kenmerk, meldingsdatum, putten]);

  const heeftKop = titel || inrichtingKenmerk || houder || bron || badge;

  return (
    <div className="mf">
      {heeftKop && (
      <header className="mf-kop">
        <div>
          {titel && <h2 className="mf-titel">{titel}</h2>}
          {(inrichtingKenmerk || houder) && (
            <p className="mf-onderschrift">
              {inrichtingKenmerk && (
                <>Inrichting <span className="mono">{inrichtingKenmerk}</span></>
              )}
              {inrichtingKenmerk && houder && ' — '}
              {houder && (
                <>{houder.naam} <span className="mono mf-kenmerk">{houder.kenmerk}</span></>
              )}
            </p>
          )}
          {bron && (
            <p className="mf-bron">
              {bron.bestand} — overgenomen uit “{bron.herkomst === 'tekst' ? 'tekst' : '.json'}”
            </p>
          )}
        </div>
        {badge && (
          <span className={`mf-badge ${controleren ? 'is-bijlage' : ''}`}>
            {controleren ? 'Via bijlage' : 'Handmatig'}
          </span>
        )}
      </header>
      )}

      <Kaart titel="Melding">
        <div className="mf-rij">
          {/* Bij lezen is de soort kenmerk geen keuze meer maar het label zelf,
              dus dan staat het kenmerk vooraan in plaats van in de rechterhelft
              met een gat ernaast. */}
          {bekijken ? (
            <>
              <Feit label={waarden.kenmerkType === 'zaaknummer' ? 'Zaaknummer' : 'Verzoeknummer'}
                waarde={waarden.kenmerk} />
              <div />
            </>
          ) : (
            <>
              <Veld label="Soort kenmerk">
                <Rondjes
                  naam="kenmerkType" richting="naast"
                  waarde={waarden.kenmerkType}
                  onChange={(v) => zet({ kenmerkType: v })}
                  opties={[
                    { waarde: 'verzoeknummer', label: 'Verzoeknummer' },
                    { waarde: 'zaaknummer', label: 'Zaaknummer' },
                  ]}
                />
              </Veld>
              <Veld label={waarden.kenmerkType === 'zaaknummer' ? 'Zaaknummer' : 'Verzoeknummer'}>
                <input className="mf-invoer" value={waarden.kenmerk}
                  onChange={(e) => zet({ kenmerk: e.target.value })} />
              </Veld>
            </>
          )}
        </div>

        <div className="mf-rij">
          {(bekijken || waarden.kenmerkType === 'verzoeknummer') ? (
            <Feit label="Meldingsdatum" waarde={datumNL(meldingsdatum)} />
          ) : (
            <Veld label="Meldingsdatum">
              <input className="mf-invoer" type="date" value={waarden.meldingsdatum || ''}
                onChange={(e) => zet({ meldingsdatum: e.target.value })} />
            </Veld>
          )}
          <div />
        </div>
      </Kaart>

      <Kaart titel="Gebruiksrecht">
        <div className="mf-rij">
          <Feit label="Inwerkingtreding" waarde={datumNL(inwerking)} />
          <div />
        </div>
        {controleren ? (
          <>
            <div className="mf-rij">
              <Feit label="Doel" waarde={doel?.label} />
            </div>
            <div className="mf-rij">
              <Feit label="Maximale pompcapaciteit"
                waarde={waarden.pompcapaciteit ? `${waarden.pompcapaciteit} m³/u` : null} />
              <Feit label="Bedrijfsomvang"
                waarde={waarden.hectare ? `${waarden.hectare} ha` : null} />
            </div>
          </>
        ) : (
          <>
            <Veld label="Doel">
              <Rondjes
                naam="doel" waarde={waarden.doel}
                onChange={(v) => {
                  const d = DOELEN.find((o) => o.waarde === v);
                  zet({ doel: v, pompcapaciteit: d ? String(d.pomp) : waarden.pompcapaciteit });
                }}
                opties={DOELEN.map((d) => ({ waarde: d.waarde, label: d.label }))}
              />
            </Veld>
            <div className="mf-rij">
              <Veld label="Maximale pompcapaciteit">
                <span className="mf-met-eenheid">
                  <input className="mf-invoer" value={waarden.pompcapaciteit}
                    onChange={(e) => zet({ pompcapaciteit: e.target.value })} />
                  <span className="mf-eenheid">m³/u</span>
                </span>
              </Veld>
              <Veld label="Bedrijfsomvang">
                <span className="mf-met-eenheid">
                  <input className="mf-invoer" value={waarden.hectare}
                    onChange={(e) => zet({ hectare: e.target.value })} />
                  <span className="mf-eenheid">ha</span>
                </span>
              </Veld>
            </div>
          </>
        )}
      </Kaart>

      {/* Twee taken, twee vormen. Controleren is lezen en vergelijken: dat doet
          een tabel het best. Zelf samenstellen is aanwijzen: dat doet de kaart
          met de kolom ernaast het best — dezelfde indeling die de melder in
          het meldingsformulier al voor zich krijgt. */}
      <div className="card mf-kaart">
        {controleren ? (
          <PuttenPaneel
            putten={putten}
            onChange={onPuttenChange}
            modus={modus}
            registerPutten={registerPutten}
            gebieden={gebieden}
            bijlage={bijlage}
            hoogte={320}
          />
        ) : (
          <PuttenKaart
            putten={putten}
            onChange={onPuttenChange}
            registerPutten={registerPutten}
            gebieden={gebieden}
            bijlage={bijlage}
            hoogte={520}
          />
        )}
      </div>

      {fout && <div className="alert alert-error" style={{ marginTop: 4 }}>{fout}</div>}

      <footer className="mf-voet">
        {!bekijken && !compleet && <span className="mf-voet-stand">Nog niet compleet</span>}
        {extraActie && (
          <button
            className={`btn ${extraActie.gevaarlijk ? 'btn-danger' : 'btn-ghost'}`}
            onClick={extraActie.onClick}
            disabled={bezig}
          >{extraActie.label}</button>
        )}
        <button className="btn btn-ghost" onClick={onAnnuleer} disabled={bezig}>Annuleren</button>
        {/* Lezen kan altijd; alleen opslaan hangt af van of het compleet is. */}
        <button className="btn btn-primary" onClick={onOpslaan}
          disabled={bezig || (!bekijken && !compleet)}>
          {bezig ? 'Bezig…' : primair}
        </button>
      </footer>
    </div>
  );
}
